import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useProximity } from '@/src/renderer/hooks/useProximity'
import { useCanvasSplitDrag } from './hooks/useCanvasSplitDrag'
import type { CanvasDragHandleProps } from './CanvasDragHandle.types'
import './CanvasDragHandle.scss'

/** Die Inhaltskarte — ihre rechte Kante ist die Kante, auf der der geschlossene
 *  Griff liegt. Als Selektor und nicht als gerechneter Abstand: Siehe `measure`. */
const CONTENT_CARD_SELECTOR = '.main-layout__content'

/**
 * Das **Drag Handle** — der Griff auf der Kante, der die Grenze zwischen
 * **Aufgabenliste** und **Projekt-Canvas** verschiebt. Siehe „Drag Handle" in
 * CONTEXT.md sowie ADR 0009 (warum es keiner Seite gehört) und ADR 0010 (warum
 * es im Layout gar nicht mehr vorkommt).
 *
 * **Es hängt per Portal am `document.body`, in JEDEM Zustand**, und liegt mit
 * `position: fixed` auf einer gemessenen Lage. Das ist keine Vorliebe, sondern
 * die Folge von drei Dingen (ADR 0010): Innerhalb des Tab-Slots klemmen drei
 * `overflow: hidden`, ein `fixed` im Content bliebe unter der SVG-Kontur der
 * Karte (`z-index: 5` gegen `.window-content`s 4), und ein Portal NUR für den
 * geschlossenen Zustand risse die Geste beim Öffnen mitten entzwei — React
 * hängte den Knoten neu ein, und mit ihm gingen Pointer-Capture, Listener,
 * `pressed` und die Nähe-Erkennung.
 *
 * **Geteilt im Ruhezustand unsichtbar, geschlossen dauerhaft da.** Der
 * Unterschied hat einen Grund: Geteilt gibt es eine sichtbare Naht zwischen
 * den Spalten, die das Ziehen andeutet — geschlossen deutet nichts darauf hin,
 * dass die Kante etwas hergibt. Dort ist der Griff selbst der Hinweis.
 *
 * Geteilt zeigt es sich über `useProximity` — dieselbe Nähe-Erkennung wie beim
 * Liste/Stapel-Umschalter (`ButtonGroup`) und den Abschnittsköpfen. Der
 * Standardradius von 96px deckt die **Werkzeug-Pille** mit ab, es braucht also
 * keine eigene Zone: Wer die Pille anfährt, sieht das Handle.
 *
 * Sichtbar ist es dann bei `nah ODER angefasst`. Das `pressed` ist kein
 * Beiwerk — ohne es verschwände das Handle mitten in der Geste: Es bleibt an
 * der Mindestbreite der Liste stehen, der Zeiger läuft weiter, und nach 96px meldet die
 * Nähe-Erkennung „weg", obwohl die Taste noch unten ist.
 *
 * **Der Aufrufer darf es bei inaktivem Tab nicht rendern.** Außerhalb des
 * Tab-Slots versteckt dessen `display: none` nichts mehr; ein Hintergrund-Tab
 * ließe seinen Griff sonst über dem sichtbaren schweben.
 *
 * @example
 * isActiveTab && (
 *   <CanvasDragHandle
 *     variant={panelState === 'closed' ? 'closed' : 'split'}
 *     enabled={isActiveTab}
 *     panel={panelElement}
 *     getRect={getRect}
 *     getState={getPanelState}
 *     layoutKey={`${panelState}:${effectiveRatio}`}
 *     onChange={applyDrag}
 *     onCommit={commitDrag}
 *     onDoubleClick={handleDoubleClick}
 *   />
 * )
 */
export const CanvasDragHandle: React.FC<CanvasDragHandleProps> = ({
  variant,
  enabled,
  panel,
  getRect,
  getState,
  getFloor,
  layoutKey,
  onChange,
  onCommit,
  onDoubleClick,
  onActiveChange,
}) => {
  const { t } = useTranslation()
  const handleRef = useRef<HTMLDivElement>(null)

  /*
    **Dauerhaft sichtbar ist nur der Griff der nackten Kartenkante** (`closed`).
    Dort gibt es nichts, was das Ziehen andeutete — der Griff selbst ist der
    Hinweis, und eine Nähe-Erkennung liefe ohne Wirkung.

    Geteilt deutet die Naht es an, dort genügt die Nähe (96px).

    Am **Canvas-Anriss** ist der Streifen selbst der Hinweis: Der Griff kommt,
    sobald der Zeiger ÜBER ihm steht, und bleibt sonst weg (Sven, 13.09.2026).
    Gemessen wird deshalb nicht am 16px schmalen Griff, sondern am PANEL — das
    ist im Anriss genau der Streifen. Die 8px Radius sind kein Spielraum,
    sondern die halbe Griffbreite: Er liegt mittig auf der linken Streifenkante,
    seine äußere Hälfte also davor. Ohne sie verschwände er, sobald man ihn
    anfassen will.
  */
  const alwaysVisible = variant === 'closed'
  const panelRef = useRef(panel)
  panelRef.current = panel
  const nearTarget = variant === 'peek' ? panelRef : handleRef
  const near = useProximity(nearTarget, {
    enabled: enabled && !alwaysVisible,
    radius: variant === 'peek' ? 8 : undefined,
  })
  const { pressed, startDrag } = useCanvasSplitDrag({ getRect, getState, getFloor, onChange, onCommit })

  const active = alwaysVisible || near || pressed

  // Über einen Effekt und nicht im Rumpf: Der Aufrufer setzt daraufhin seinen
  // eigenen Zustand (der **Randpfeil** links weicht), und das gehört nicht in
  // die Renderphase eines Kindes.
  useEffect(() => {
    onActiveChange?.(active)
  }, [active, onActiveChange])

  /**
   * Die Lage, gemessen und DIREKT auf den Knoten geschrieben — kein State.
   *
   * Dieselbe Begründung wie beim `--active-canvas-split-ratio` im Panel: Der
   * Wert ändert sich während eines Zugs in jedem Frame, über State zöge das ein
   * Rendern des gesamten Panels nach sich. `style.left` kostet nichts.
   *
   * | | senkrecht | waagerecht |
   * |---|---|---|
   * | geschlossen | Mitte zwischen Kartenoberkante und Zeilenunterkante | rechte Kante der Inhaltskarte |
   * | geteilt / maximiert | dieselbe | linke Kante des Panels |
   * | Anriss | dieselbe | linke Kante des Panels (des Streifens) |
   *
   * **Senkrecht zählt die KARTE, nicht die Zeile** — sonst säße der Griff
   * geschlossen rund 20px tiefer als geöffnet. Grund ist die Breadcrumb: Bei
   * offenem Canvas liegt sie absolut über der Karte (`MainLayout.scss`), bei
   * geschlossenem belegt sie eine Zeile im Fluss. Die Body-Zeile beginnt damit
   * geschlossen um deren Höhe tiefer, ihre Mitte um die halbe. Die Oberkante
   * der Karte kennt die Breadcrumb nicht, und beide Größen fallen auf dieselbe
   * Mitte. Geöffnet ändert sich dadurch nichts: Dort liegen Karten- und
   * Zeilenoberkante ohnehin aufeinander.
   *
   * Die Kartenkante wird GESUCHT statt gerechnet. `rowRect.right + 7` wäre
   * kürzer, aber die 7 stehen als `$scrollbar-gutter` im SCSS — eine zweite
   * Kopie hier wären zwei Quellen der Wahrheit, die genau im Randfall
   * auseinanderlaufen. Findet sich die Karte nicht (Story, Test ohne Hülle),
   * gilt die Zeile für beide Achsen.
   *
   * Die eigenen Maße kommen aus `offsetWidth/Height` und nicht als Konstante:
   * Die 16 × 36 stehen im SCSS und sollen dort allein stehen.
   */
  /**
   * Was sich beim Wachsen und Schrumpfen des Canvas NICHT ändert.
   *
   * Von den drei Rechtecken, die die Lage ergeben, bewegt sich während einer
   * Breitenbewegung genau eines: die linke Kante des Panels. Die Oberkante der
   * Karte, die Unterkante der Zeile und ihre rechte Kante stehen still — die
   * Karte wächst nach links, nicht nach oben oder rechts.
   *
   * Sie trotzdem in jedem Bild zu lesen kostete zwei erzwungene Layout-
   * Durchgänge je Bild, und der Griff hängt an jedem Bild der Bewegung
   * (`ResizeObserver`). Gemerkt werden sie deshalb, bis sich etwas ändert, was
   * sie wirklich betrifft: eine HÖHENänderung von Zeile oder Karte, ein
   * Größenwechsel des Panels (`layoutKey`) oder das Fenster.
   */
  const anchorRef = useRef<{ top: number; bottom: number; right: number } | null>(null)
  /**
   * Die eigenen Maße (16 × 36 aus dem Stylesheet). `offsetWidth`/`offsetHeight`
   * sind ebenfalls Layout-Lesevorgänge; sie ändern sich nie.
   */
  const sizeRef = useRef<{ width: number; height: number } | null>(null)

  const measure = useCallback(() => {
    const node = handleRef.current
    const row = panel?.parentElement
    if (!node || !panel || !row) return

    if (!anchorRef.current) {
      const rowRect = row.getBoundingClientRect()
      // TabStack-Falle (CLAUDE.md): Ein Teilbaum hinter `display: none` misst
      // durchweg 0 — daraus eine Lage zu rechnen setzte den Griff in die linke
      // obere Ecke des Fensters.
      if (rowRect.width === 0 || rowRect.height === 0) return
      const card = panel.closest(CONTENT_CARD_SELECTOR)?.getBoundingClientRect() ?? null
      anchorRef.current = {
        top: card?.top ?? rowRect.top,
        bottom: rowRect.bottom,
        right: card?.right ?? rowRect.right,
      }
    }
    if (!sizeRef.current) {
      sizeRef.current = { width: node.offsetWidth, height: node.offsetHeight }
    }

    const { top, bottom, right } = anchorRef.current
    const { width, height } = sizeRef.current
    // Das eine, was sich bewegt: Geschlossen liegt der Griff auf der
    // Kartenkante, sonst auf der linken Kante des Panels.
    const edgeX = variant === 'closed' ? right : panel.getBoundingClientRect().left

    node.style.left = `${edgeX - width / 2}px`
    node.style.top = `${top + (bottom - top - height) / 2}px`
  }, [panel, variant])

  /**
   * Neu messen, sobald sich etwas bewegt haben könnte.
   *
   * Der Beobachter sitzt auf Panel UND Zeile: Das Panel liefert jede
   * Breitenänderung (auch die animierten, Frame für Frame — `ResizeObserver`
   * meldet nach dem Layout und vor dem Paint, der Griff hängt also nicht
   * hinterher), die Zeile jede Fenster-, Navigations- und Breadcrumb-Änderung.
   *
   * Genau deshalb trägt der Griff auch KEINE eigene `transition` auf `left`:
   * Die Kante wird ohnehin jeden Frame nachgeführt, ein Übergang liefe ihr nur
   * mit eigener Kurve hinterher.
   *
   * Eine Rückkopplung gibt es nicht: Geschrieben wird `left`/`top` eines
   * `fixed`-Elements, das beobachtet niemand.
   */
  useLayoutEffect(() => {
    // Ein Größenwechsel kann die senkrechte Lage verschoben haben — die
    // Breadcrumb verlässt bei offenem Canvas den Fluss.
    anchorRef.current = null
    measure()

    const row = panel?.parentElement
    if (!panel || !row) return

    /**
     * Die HÖHEN aus den Einträgen, nicht aus einer Messung: `contentRect`
     * liegt dem Beobachter ohnehin bei und kostet nichts. Ändert sich eine,
     * ist die gemerkte senkrechte Lage hin; ändert sich nur die Breite — der
     * Normalfall beim Auf- und Zugehen —, bleibt sie gültig.
     */
    let lastHeights = ''
    const observer = new ResizeObserver(entries => {
      const heights = entries.map(entry => Math.round(entry.contentRect.height)).join()
      if (heights !== lastHeights) {
        lastHeights = heights
        anchorRef.current = null
      }
      measure()
    })
    observer.observe(panel)
    observer.observe(row)

    // Das Fenster verschiebt beides, ohne dass eine Höhe sich ändern müsste.
    const onResize = () => { anchorRef.current = null; measure() }
    window.addEventListener('resize', onResize)

    return () => {
      observer.disconnect()
      window.removeEventListener('resize', onResize)
    }
  }, [measure, panel, layoutKey])

  return createPortal(
    <div
      ref={handleRef}
      className={`canvas-drag-handle canvas-drag-handle--${variant}${active ? ' canvas-drag-handle--active' : ''}${pressed ? ' canvas-drag-handle--pressed' : ''}`}
      role="separator"
      aria-orientation="vertical"
      /*
        Das Label sagt, was ein Zug TUT, nicht wann der Griff zu sehen ist.
        Geschlossen wie am Anriss holt er den Canvas herein, geteilt verschiebt
        er die Grenze. Es hing einmal an `alwaysVisible` — das ging nur so
        lange gut, wie der Anriss seinen Griff dauerhaft zeigte.
      */
      aria-label={t(variant === 'split'
        ? 'projectCanvas.dragHandle.label'
        : 'projectCanvas.dragHandle.labelClosed')}
      onPointerDown={startDrag}
      // Nicht geteilt → die geteilte Ansicht herstellen; geteilt → auf
      // halbe/halbe zurücksetzen. Welcher Fall vorliegt, entscheidet das Panel.
      // Dass daraus nicht drei Schreibvorgänge werden — zwei aus den Klicks,
      // einer aus dem Zurücksetzen —, verhindert die Bewegungsschwelle in
      // `useCanvasSplitDrag`.
      onDoubleClick={onDoubleClick}
    >
      {/*
        Die Schraffur als eigenes Element, nicht als zweite Hintergrundschicht
        am Griff selbst: Sie muss INNERHALB des 2px-Rahmens liegen, und ein
        Verlauf am Element würde unter ihm hindurchlaufen. Entspricht zugleich
        dem Aufbau im Figma (Pille mit einer Schraffur-Ebene darin).
      */}
      <span className="canvas-drag-handle__hatch" aria-hidden="true" />
    </div>,
    document.body,
  )
}
